/**
 * Per-endpoint persistence of the last-sent request body + path params.
 *
 * Stored in `window.localStorage` under key
 * `op-emulator/endpoint-state/<endpointId>` so the user's edits survive
 * page reload AND switching between endpoints. Cleared per-endpoint
 * via "Reset to default" button on the form (Day 3+).
 *
 * Failure mode: localStorage may be disabled or full. We swallow errors
 * and degrade to "no persistence" rather than crashing the UI.
 */

const PREFIX = 'op-emulator/endpoint-state/v1/';

export interface PersistedEndpointState {
  pathParams: Record<string, string>;
  bodyText: string;
  /** ISO timestamp of last save (for staleness display, optional). */
  lastSavedAt: string;
}

export function loadState(endpointId: string): PersistedEndpointState | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + endpointId);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as PersistedEndpointState;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.bodyText === 'string' &&
      typeof parsed.pathParams === 'object'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveState(
  endpointId: string,
  state: Omit<PersistedEndpointState, 'lastSavedAt'>,
): void {
  try {
    const full: PersistedEndpointState = {
      ...state,
      lastSavedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(PREFIX + endpointId, JSON.stringify(full));
  } catch {
    // ignore — localStorage full / disabled in private mode
  }
}

export function clearState(endpointId: string): void {
  try {
    window.localStorage.removeItem(PREFIX + endpointId);
  } catch {
    // ignore
  }
}

/**
 * Tiny string-only persistence helpers. Day 3 added pages (HMAC
 * validator, future scenario runner) need to remember scalar inputs
 * across reloads — same fail-soft pattern as `loadState` / `saveState`,
 * but without the JSON envelope. Keys are caller-namespaced; we don't
 * add a prefix here so callers can choose collision-safe identifiers.
 */
export function loadString(key: string, fallback: string): string {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : raw;
  } catch {
    return fallback;
  }
}

export function saveString(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore — full / disabled
  }
}
